import * as THREE from "three";
import liquidMetalFrag from "./shaders/liquid-metal.frag?raw";
import liquidMetalVert from "./shaders/liquid-metal.vert?raw";
import "./styles/typography.css";
import "./styles/base.css";
import { initMotion, initCalculator } from "./motion";

// ---------- Hero WebGL ----------

function initHero(): (() => void) | null {
	const canvas = document.getElementById(
		"hero-canvas",
	) as HTMLCanvasElement | null;
	if (!canvas) return null;

	const reducedMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	).matches;
	const isMobile = window.matchMedia("(max-width: 768px)").matches;

	let renderer: THREE.WebGLRenderer;
	try {
		renderer = new THREE.WebGLRenderer({
			canvas,
			antialias: true,
			powerPreference: "high-performance",
			alpha: false,
		});
	} catch {
		document.body.classList.add("no-webgl");
		return null;
	}
	renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // gamma in shader
	renderer.setPixelRatio(isMobile ? 1 : Math.min(window.devicePixelRatio, 2));

	const heroEl = canvas.parentElement!;
	const sizeToHero = () => {
		const r = heroEl.getBoundingClientRect();
		renderer.setSize(r.width, r.height, false);
	};
	sizeToHero();

	const scene = new THREE.Scene();
	const camera = new THREE.PerspectiveCamera(
		50,
		canvas.clientWidth / canvas.clientHeight,
		0.1,
		10,
	);
	camera.position.set(0, 0, 3);

	const uniforms = {
		uTime: { value: 0 },
		uResolution: {
			value: new THREE.Vector2(canvas.clientWidth, canvas.clientHeight),
		},
		uMouse: { value: new THREE.Vector2(0.5, 0.5) },
		uPixelRatio: { value: renderer.getPixelRatio() },
	};

	const geometry = new THREE.PlaneGeometry(4, 4, 1, 1);
	const material = new THREE.ShaderMaterial({
		vertexShader: liquidMetalVert,
		fragmentShader: liquidMetalFrag,
		uniforms,
		transparent: false,
		depthWrite: false,
	});

	const mesh = new THREE.Mesh(geometry, material);
	scene.add(mesh);

	const mouseTarget = new THREE.Vector2(0.5, 0.5);
	const onPointer = (e: PointerEvent) => {
		mouseTarget.set(
			e.clientX / window.innerWidth,
			1.0 - e.clientY / window.innerHeight,
		);
	};
	window.addEventListener("pointermove", onPointer);

	const onResize = () => {
		sizeToHero();
		camera.aspect = canvas.clientWidth / canvas.clientHeight;
		camera.updateProjectionMatrix();
		uniforms.uResolution.value.set(canvas.clientWidth, canvas.clientHeight);
	};
	window.addEventListener("resize", onResize);

	const clock = new THREE.Clock();
	renderer.setAnimationLoop(() => {
		const dt = clock.getDelta();
		if (!reducedMotion) uniforms.uTime.value += dt;
		uniforms.uMouse.value.lerp(mouseTarget, 0.08);
		renderer.render(scene, camera);
	});

	return () => {
		renderer.setAnimationLoop(null);
		window.removeEventListener("pointermove", onPointer);
		window.removeEventListener("resize", onResize);
		renderer.dispose();
		geometry.dispose();
		material.dispose();
	};
}

// ---------- Calculator wiring ----------

function wireCalculator(): void {
	const select = document.getElementById(
		"calc-turnover",
	) as HTMLSelectElement | null;
	if (!select) return;

	// Formula: cart abandonment 70% (Baymard) × recovery uplift 18% (Klaviyo) × turnover.
	// Assumes 100% of turnover is cartable (simplification — model upper bound).
	const computeRecovery = (turnover: number): number => {
		const abandonRate = 0.7;
		const recoveryUplift = 0.18;
		return Math.round(turnover * abandonRate * recoveryUplift);
	};

	const update = () => {
		const turnover = Number(select.value);
		const recovery = computeRecovery(turnover);
		const updateFn = (
			window as unknown as { __calcUpdate?: (n: number) => void }
		).__calcUpdate;
		updateFn?.(recovery);
	};

	select.addEventListener("change", update);
	// Initial value (after motion init exposes __calcUpdate).
	queueMicrotask(update);
}

// ---------- Boot ----------

window.addEventListener("DOMContentLoaded", () => {
	initHero();
	initMotion();
	initCalculator();
	wireCalculator();
});
